import { apiClient, createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../../api/apiClient';
import { buildStandardFilterRequest } from '../../../utils/filterBuilder';
import type { AppliedFiltersSnapshot } from '../OnTimeDispatchDashboard';

export type TrendTimeframe = 'YEAR' | 'PERIOD' | 'WEEK' | 'DAY';
export type TrendTimeframeGrain = 'year' | 'period' | 'week' | 'day';

export interface TrendFilters {
  region: string[] | null;
  market: string[] | null;
  location: string[] | null;
  locationType: string[] | null;
  year: number[] | null;
  period: number[] | null;
  week: number[] | null;
  startDate: string | null;
  endDate: string | null;
}

export interface ChartConfiguration {
  timeframe: TrendTimeframe;
}

export interface TrendRequest {
  filters: TrendFilters;
  chartConfiguration: ChartConfiguration;
}

export interface TrendPoint {
  label: string;
  value: number;
  // Present only for PERIOD-timeframe points: the fiscal year that period belongs to.
  year?: number;
  // Present only for WEEK-timeframe points: the fiscal period that week belongs to.
  period?: number;
}

export interface YAxis {
  min: number;
  max: number;
}

export interface TrendResponseData {
  metric: string;
  target: number | null;
  subtitle: string | null;
  timeframe: TrendTimeframe;
  data: TrendPoint[];
  yAxis: YAxis | null;
  noData: boolean;
  message: string | null;
}

export interface ApiEnvelope<T> {
  data: T;
  executionTimeMs: number;
  asOfDate: string;
  isApproximate: boolean;
  message: string | null;
}

export interface ApiErrorEnvelope {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
  fieldErrors?: Record<string, string> | Array<{ field: string; message: string }>;
}

export class TrendApiError extends Error {
  status: number;
  path?: string;
  fieldErrors?: ApiErrorEnvelope['fieldErrors'];

  constructor(message: string, status: number, path?: string, fieldErrors?: ApiErrorEnvelope['fieldErrors']) {
    super(message);
    this.name = 'TrendApiError';
    this.status = status;
    this.path = path;
    this.fieldErrors = fieldErrors;
  }
}

const TIMEFRAME_MAP: Record<TrendTimeframeGrain, TrendTimeframe> = {
  year: 'YEAR',
  period: 'PERIOD',
  week: 'WEEK',
  day: 'DAY',
};

// region/market/location/locationType are independent array filters on this endpoint (not resolved to location IDs).
const toIdArray = (items?: { id: string | number; displayText: string }[]): string[] | null => {
  if (!items || items.length === 0) return null;
  const ids = items.map((item) => String(item.id).trim()).filter(Boolean);
  return ids.length > 0 ? ids : null;
};

// Shared by every trend/variance endpoint — the filters shape is identical across all of them.
export function buildTrendFilters(appliedFilters: AppliedFiltersSnapshot): TrendFilters {
  // KPIFilterDropdownItem.id is string | number while the shared filter builder expects string ids only.
  const standard = buildStandardFilterRequest(
    appliedFilters as unknown as Parameters<typeof buildStandardFilterRequest>[0],
    []
  );

  return {
    region: toIdArray(appliedFilters.selectedRegions),
    market: toIdArray(appliedFilters.selectedMarkets),
    location: toIdArray(appliedFilters.selectedLocations),
    locationType: toIdArray(appliedFilters.selectedLocationTypes),
    year: standard.year && standard.year.length > 0 ? standard.year : null,
    period: standard.period && standard.period.length > 0 ? standard.period : null,
    week: standard.week && standard.week.length > 0 ? standard.week : null,
    startDate: standard.deliveryDateStart ?? null,
    endDate: standard.deliveryDateEnd ?? null,
  };
}

// Shared by every trend endpoint (Completed % Trend, On-Time % Trend, ...) — the request shape is identical.
export function buildTrendRequest(
  appliedFilters: AppliedFiltersSnapshot,
  timeframeGrain: TrendTimeframeGrain
): TrendRequest {
  return {
    filters: buildTrendFilters(appliedFilters),
    chartConfiguration: {
      timeframe: TIMEFRAME_MAP[timeframeGrain],
    },
  };
}

const api = createApiClient();

interface PostTrendOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
}

function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  return !!value && typeof value === 'object' && 'message' in value && 'status' in value;
}

// Shared POST + error handling for every trend endpoint; only the URL and fallback message differ per metric.
async function postTrend(
  endpoint: string,
  payload: TrendRequest,
  metricLabel: string,
  options?: PostTrendOptions
): Promise<ApiEnvelope<TrendResponseData>> {
  try {
    const response = await api.post<ApiEnvelope<TrendResponseData>>(
      endpoint,
      payload,
      {
        params: {
          clientTimestamp: getLocalTimestamp(),
          clientTimezone: getTimezoneOffsetString(),
        },
        timeout: options?.timeoutMs,
        signal: options?.signal,
      }
    );
    return response.data;
  } catch (error: any) {
    const responseData = error?.response?.data;
    if (isApiErrorEnvelope(responseData)) {
      throw new TrendApiError(responseData.message, responseData.status, responseData.path, responseData.fieldErrors);
    }
    const status = error?.response?.status ?? 0;
    const message = error?.message || `Failed to load ${metricLabel} data`;
    throw new TrendApiError(message, status);
  }
}

const COMPLETED_TREND_ENDPOINT = '/ontime-dispatch/completed-trend';
const ONTIME_TREND_ENDPOINT = '/ontime-dispatch/ontime-trend';
const DRIVER_ARRIVED_TREND_ENDPOINT = '/ontime-dispatch/driver-arrived-trend';

export async function getCompletedTrend(
  payload: TrendRequest,
  options?: PostTrendOptions
): Promise<ApiEnvelope<TrendResponseData>> {
  return postTrend(COMPLETED_TREND_ENDPOINT, payload, 'Completed % Trend', options);
}

export async function getOnTimeTrend(
  payload: TrendRequest,
  options?: PostTrendOptions
): Promise<ApiEnvelope<TrendResponseData>> {
  return postTrend(ONTIME_TREND_ENDPOINT, payload, 'On-Time % Trend', options);
}

export async function getDriverArrivedTrend(
  payload: TrendRequest,
  options?: PostTrendOptions
): Promise<ApiEnvelope<TrendResponseData>> {
  return postTrend(DRIVER_ARRIVED_TREND_ENDPOINT, payload, 'Driver Arrived as Expected % Trend', options);
}

